import { Flex, Skeleton, Text } from '@chakra-ui/react';
import React from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { usePortalBusServicingCyclesForDealQuery } from '~/backup/shared/views/deal-overview/dealOverview.gql.generated';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { ClientCreditAgreementWip } from '~/packages/creditAgreements/ui/creditAgreementWip/ClientCreditAgreementWip';
import {
  CreditAgreement,
  CreditAgreementFullArtifactWipConfigType,
} from '~/packages/creditAgreements/ui/types';

type ArtifactComponent = React.ComponentType<{
  organizationId: string;
  creditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
}>;

/**
 * Shared editor for a single BUS credit-agreement config section (Report Config
 * or Deliverable Config). Resolves the deal's single `backupServicing` facility
 * from the org-scoped servicing-cycle list, then renders the client CA-WIP editor
 * with the given section body injected. Editable for admins, read-only for other
 * lender users (MEMBER / CONTRIBUTOR).
 */
export const BusCaConfigEditor = ({
  artifactComponent,
  emptyLabel,
}: {
  artifactComponent: ArtifactComponent;
  /** Section label shown in the "no facility yet" empty state. */
  emptyLabel: string;
}): React.ReactElement => {
  const { user } = useUserAuthOrThrow();
  const { implementationId } = useParams();
  const readOnly = user.roleType !== 'ADMIN';

  const { data, loading, error } = usePortalBusServicingCyclesForDealQuery({
    variables: { implementationId: implementationId ?? '' },
    skip: !implementationId,
    fetchPolicy: 'cache-first',
  });

  if (!implementationId) return <Navigate to="/portfolio" replace />;

  if (loading) return <Skeleton height="150px" />;

  const creditFacilityId =
    data?.portalBusServicingCycles[0]?.creditFacilityId ?? null;

  if (error || !creditFacilityId) {
    return (
      <Flex direction="column" gap="0.5rem">
        <Typography variant="section-title">{emptyLabel}</Typography>
        <Text color={colors.gray[500]}>
          This deal has no configurable servicing facility yet. Configuration
          becomes available once the deal has an active servicing cycle.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column">
      {readOnly && (
        <Text mb="1rem" color={colors.gray[500]}>
          You have read-only access to this configuration. Contact a lender
          admin to make changes.
        </Text>
      )}
      <ClientCreditAgreementWip
        organizationId={user.organization.id}
        implementationId={implementationId}
        creditFacilityId={creditFacilityId}
        onDonePath={`/deals/${implementationId}/settings`}
        ArtifactComponent={artifactComponent}
        simplifiedHeader
        readOnly={readOnly}
      />
    </Flex>
  );
};
